/**
 * Sistema de Filtros Avançados - Tipos Base
 * Suporte a filtros dinâmicos para todas as colunas da tabela
 */

export type FilterType = 'text' | 'select' | 'multiselect' | 'number' | 'date' | 'boolean' | 'custom'

export type FilterOperator =
  // Text operators
  | 'contains' | 'notContains' | 'equals' | 'notEquals' 
  | 'startsWith' | 'endsWith'
  // Number/Date operators  
  | 'greaterThan' | 'lessThan' | 'greaterThanOrEqual' | 'lessThanOrEqual'
  | 'between' | 'notBetween'
  // Array operators
  | 'in' | 'notIn'
  // Null operators
  | 'isEmpty' | 'isNotEmpty'
  // Custom operators
  | 'cardFilter'

export interface DynamicFilter {
  id: string
  columnId: string
  operator: FilterOperator
  value: any
  type: FilterType
  label: string
  enabled: boolean
}

export interface FilterConfig {
  columnId: string
  label: string
  type: FilterType
  operators: FilterOperator[]
  options?: FilterOption[] // Para selects
  placeholder?: string
  category: string
}

export interface FilterOption {
  value: any
  label: string
  count?: number // Para mostrar quantos registros tem esse valor
}

export interface FilterPreset {
  id: string
  name: string
  description?: string
  filters: Omit<DynamicFilter, 'id'>[]
  isSystem?: boolean // Presets do sistema vs usuário
}

export interface ActiveFilterChip {
  filterId: string
  label: string
  displayValue: string
  onRemove: () => void
}

export interface FilterOperatorOption {
  value: FilterOperator
  label: string
  supportedTypes: FilterType[]
}

// Mapeamento de operadores para tipos
export const FILTER_OPERATORS: FilterOperatorOption[] = [
  // Text operators
  { value: 'contains', label: 'Contém', supportedTypes: ['text'] },
  { value: 'notContains', label: 'Não contém', supportedTypes: ['text'] },
  { value: 'equals', label: 'Igual a', supportedTypes: ['text', 'select', 'number', 'date', 'boolean'] },
  { value: 'notEquals', label: 'Diferente de', supportedTypes: ['text', 'select', 'number', 'date', 'boolean'] },
  { value: 'startsWith', label: 'Começa com', supportedTypes: ['text'] },
  { value: 'endsWith', label: 'Termina com', supportedTypes: ['text'] },
  
  // Number/Date operators
  { value: 'greaterThan', label: 'Maior que', supportedTypes: ['number', 'date'] },
  { value: 'lessThan', label: 'Menor que', supportedTypes: ['number', 'date'] },
  { value: 'greaterThanOrEqual', label: 'Maior ou igual', supportedTypes: ['number', 'date'] },
  { value: 'lessThanOrEqual', label: 'Menor ou igual', supportedTypes: ['number', 'date'] },
  { value: 'between', label: 'Entre', supportedTypes: ['number', 'date'] },
  { value: 'notBetween', label: 'Não está entre', supportedTypes: ['number', 'date'] },
  
  // Array operators
  { value: 'in', label: 'É um de', supportedTypes: ['select', 'multiselect'] },
  { value: 'notIn', label: 'Não é um de', supportedTypes: ['select', 'multiselect'] },
  
  // Null operators
  { value: 'isEmpty', label: 'Está vazio', supportedTypes: ['text', 'select', 'number', 'date', 'boolean'] },
  { value: 'isNotEmpty', label: 'Não está vazio', supportedTypes: ['text', 'select', 'number', 'date', 'boolean'] },
  
  // Custom operators
  { value: 'cardFilter', label: 'Card Filter', supportedTypes: ['custom'] },
]

// Utilitário para obter operadores por tipo
export const getOperatorsForType = (type: FilterType): FilterOperator[] => {
  return FILTER_OPERATORS
    .filter(op => op.supportedTypes.includes(type))
    .map(op => op.value)
}

// Utilitário para obter label do operador
export const getOperatorLabel = (operator: FilterOperator): string => {
  const found = FILTER_OPERATORS.find(op => op.value === operator)
  return found?.label || operator
}
